import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TYPES, { STATUS_TYPE } from '@v4/constants/quiz'
import AppStore from '@v4/store/appStore'
import { useQuestion } from '@v4/hooks'
import useTimer from '@v4/hooks/useTimer'
import { useSignUpData, useQueryInitialAnswers } from '@v4/components/common/Form'
import PageMetaData from '@v4/utils/data/PageMetaData'
import { INSTRUCTION_TEXT_POSITION, TYPE_REL } from './AnswerTypes.const'
import validation, { textSchema } from './validation'

import useStyles from './AnswerTypes.styled'

export const useAnswerType = props => {
	const {
		questionId,
		withBgImage,
		align,
		withShadow,
		useOldCheckboxes,
		instruction,
		type,
		media,
		instructionPosition,
		mediaPosition,
		showMedia,
		invertColors,
	} = props
	const timeoutRef = useRef(null)
	const tokens = AppStore.useStoreState(state => state.tokens)
	const [message, setMessage] = useState(null)
	const [isOpen, setOpen] = useState(false)
	const originalQuestion = useQuestion()
	const formRef = useRef()
	const captchaRef = useRef(null)
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const basename = AppStore.useStoreState(state => state.general.basename)
	const result = AppStore.useStoreState(state => state.result.data)
	const target = AppStore.useStoreState(state => state.general.targetWindow)
	const isLoading = AppStore.useStoreState(state => state.question.isLoading)
	const status = AppStore.useStoreState(state => state.question.status)
	const sendAnswer = AppStore.useStoreActions(actions => actions.question.sendAnswer)
	const { prefill } = AppStore.useStoreActions(actions => actions.signUp)
	const classes = useStyles(props)
	const getTime = useTimer(originalQuestion.id)
	const initialAnswers = useQueryInitialAnswers()
	const { prefillValues } = useSignUpData(initialAnswers)

	const ActiveType = useMemo(() => {
		return TYPE_REL[type] || TYPE_REL.default
	}, [type])

	const schema = useMemo(() => {
		return (
			validation[originalQuestion.type] &&
			validation[originalQuestion.type](originalQuestion.config, originalQuestion.options.length)
		)
	}, [originalQuestion.config, originalQuestion.options.length, originalQuestion.type])

	const handleOpen = useCallback(() => {
		setOpen(true)
		clearTimeout(timeoutRef.current)
	}, [])

	const handleClose = useCallback(() => {
		setOpen(false)
		timeoutRef.current = setTimeout(() => {
			setMessage(null)
		}, 300)
	}, [])

	const handleSubmit = useCallback(
		/**
		 *
		 * @param {object} data - object
		 * @param {number|number[]} data.option - option id or array of ids
		 * @param {number} data.time_spent - time in seconds
		 * @param {number|string} data.answer - custom id
		 */
		async (data = {}) => {
			try {
				let captcha
				if (isBuilderMode) return

				if (captchaRef.current) {
					const { response } = await captchaRef.current.execute({ async: true })
					captcha = response
				}

				if (type !== TYPES.PLACEHOLDER) {
					const otherOption = _.find(originalQuestion?.options, ({ open_text: openText }) => openText)
					if (otherOption && _.includes(data.options, otherOption.value)) {
						await schema.validate(data.options)
						await textSchema({ max: 1000, required: true }).validate(data.answer)
					} else {
						await schema.validate(data.options || data.answer)
					}
				}

				const fbadId = initialAnswers.fbad_id

				const response = await sendAnswer({
					key: result.key,
					id: originalQuestion.id,
					data: {
						...data,
						time_spent: getTime(),
						...(fbadId && { fbad_id: fbadId }),
						...(captcha && { captcha }),
					},
				})

				if (response.data.result?.sing_up_required && !_.isEmpty(prefillValues)) {
					await prefill(prefillValues).catch(() => {})
				}
			} catch (e) {
				setMessage(e?.message || e?.answer || _.values(e)[0])
				handleOpen()
			}
		},
		[
			isBuilderMode,
			type,
			initialAnswers.fbad_id,
			sendAnswer,
			result.key,
			originalQuestion.id,
			originalQuestion.options,
			getTime,
			prefillValues,
			schema,
			prefill,
			handleOpen,
		],
	)

	useEffect(() => {
		if (status === STATUS_TYPE.FINISHED) {
			formRef.current.submit()
		}
		return () => {
			clearTimeout(timeoutRef.current)
		}
	}, [status])

	const preventFormSubmitOnPressEnter = useCallback(event => {
		if (event.key === 'Enter' && event.target.nodeName !== 'TEXTAREA') {
			event.preventDefault()
		}
	}, [])

	useEffect(() => {
		const form = formRef.current

		form.addEventListener('keydown', preventFormSubmitOnPressEnter)
		return () => {
			form.removeEventListener('keydown', preventFormSubmitOnPressEnter)
		}
	}, [preventFormSubmitOnPressEnter])

	const isPlaceholderType = type === TYPES.PLACEHOLDER

	return [
		{
			classes,
			withBgImage,
			withShadow,
			align,
			resultKey: result.key,
			captchaRef,
			formRef,
			ActiveType,
			tokens,
			type,
			isOpen,
			message,
			id: questionId,
			isLoading,
			useOldCheckboxes,
			target: target || undefined,
			action: `${basename}/finish`,
			instruction,
			media,
			invertColors,
			showNoQuestionMessage: status === STATUS_TYPE.NO_QUESTIONS && !isBuilderMode,
			showAnswerType:
				!!type && (isBuilderMode || ![STATUS_TYPE.FINISHED, STATUS_TYPE.NO_QUESTIONS].includes(status)),
			answers: originalQuestion.answers,
			titlePlaceholder: isPlaceholderType ? 'Write your info screen text here' : undefined,
			instructionDirection:
				instructionPosition === INSTRUCTION_TEXT_POSITION.ABOVE
					? PageMetaData.SORT_DIRECTION.DESC
					: PageMetaData.SORT_DIRECTION.ASC,
			mediaPosition,
			showMedia,
		},
		{ handleClose, handleSubmit },
	]
}
